// ABOUTME: Select field for forms, backed by the DS Autocomplete with single and multi value support.
// ABOUTME: In multi mode it offers a select-all option and collapses chips to "+n" when closed.
import { Autocomplete, Box, Chip, KeyboardArrowDownIcon, Tooltip } from '@ds/coolshop';
import { useLanguageCtx } from '@modules/language';
import { SelectFieldProps } from '@src/assets/types';
import { Option, SelectFieldOptions } from '@src/assets/types/form/SelectField.types';
import { useAuth } from '@src/hooks';
import { useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { getCGTCustomMMDispoOptionLabel } from './helpers';

interface TransformedOption {
    code: string | null;
    label: string;
}

// Sentinel code for the multi value "select all" pseudo-option
const SELECT_ALL_CODE = '__select-all__';
// Max chips shown in the multi value select input; the rest collapse into a "+n" chip
const MAX_VISIBLE_TAGS = 2;

interface TransformOptions {
    options: SelectFieldOptions['options'];
    valueLabel: SelectFieldOptions['valueLabel'];
    nameLabel: SelectFieldOptions['nameLabel'];
    dataCy: SelectFieldOptions['dataCy'];
    isEditNode: boolean;
    isMultiValue?: boolean;
    showEmptyOption?: boolean;
    showCode?: boolean;
    defaultLabel?: string;
    defaultLanguage: string;
}

const getOptionValue = (option: Option | string, property: string | undefined): string => {
    if (property && typeof option === 'object' && option !== null && property in option) {
        return option[property];
    }
    return String(option);
};

function getNameFromOption(
    option: Option,
    defaultLanguage: string,
    nameLabel: string,
    showCode?: boolean,
) {
    if (!Array.isArray(option.value)) {
        return '';
    }

    const foundValue = option.value?.find(v => v?.languageIsoCode === defaultLanguage);
    const name = foundValue?.[nameLabel] ?? option.value[0][nameLabel] ?? '';
    return showCode && option.code ? `${name} (${option.code})` : name;
}

// Function to transform the options
const transformOptions = ({
    options,
    valueLabel,
    nameLabel,
    isEditNode,
    isMultiValue,
    showEmptyOption,
    showCode,
    defaultLabel,
    defaultLanguage,
}: TransformOptions): TransformedOption[] => {
    if (!options) {
        return [];
    }

    const result: TransformedOption[] = [];

    if (showEmptyOption && !isMultiValue) {
        result.push({
            code: defaultLabel ?? null,
            label: defaultLabel ?? 'None',
        });
    }

    result.push(
        ...options.map<TransformedOption>(option => ({
            code: getOptionValue(option, valueLabel),
            label:
                isEditNode && nameLabel && typeof option === 'object'
                    ? getNameFromOption(option, defaultLanguage, nameLabel, showCode)
                    : getOptionValue(option, nameLabel),
        })),
    );

    return result;
};

function SelectFieldElement(props: SelectFieldProps) {
    const {
        id,
        name,
        defaultValue = '',
        control,
        isMultiValue = false,
        fieldOptions,
        selectFieldTitle,
        defaultLabel,
        fullWidth = true,
        readOnly = false,
        readOnlyMessage,
        disabled = false,
        onChange,
        componentsProps,
    } = props;

    const intl = useIntl();

    const { sessionData } = useAuth();

    const { field } = useController({
        name,
        control,
        defaultValue,
    });

    const [filterValue, setFilterValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const { defaultLanguage } = useLanguageCtx();

    const { valueLabel, nameLabel, dataCy, options, showEmptyOption, isEditNode, showCode } =
        fieldOptions;

    const { formatId, formatDefault } = selectFieldTitle;

    const transformedOptions = transformOptions({
        options,
        valueLabel,
        nameLabel,
        dataCy,
        isEditNode: Boolean(isEditNode),
        showEmptyOption,
        showCode,
        isMultiValue,
        defaultLabel,
        defaultLanguage,
    });

    const autocompleteValue = useMemo(() => {
        // Handle initial multi value
        if (isMultiValue) {
            return transformedOptions.filter(
                o => Array.isArray(field.value) && field.value?.find((v: any) => v === o?.code),
            );
        }
        // Handle initial value
        const option = transformedOptions?.find(o => o?.code === field?.value);
        return option?.code ? option : null;
    }, [isMultiValue, field, options]);

    const allSelected =
        isMultiValue &&
        transformedOptions.length > 0 &&
        transformedOptions.every(o => Array.isArray(field.value) && field.value.includes(o.code));

    const autocompleteOptions = isMultiValue
        ? [
              {
                  code: SELECT_ALL_CODE,
                  label: allSelected
                      ? intl.formatMessage({
                            id: 'select-field-deselect-all',
                            defaultMessage: 'Deselect all',
                        })
                      : intl.formatMessage({
                            id: 'select-field-select-all',
                            defaultMessage: 'Select all',
                        }),
              },
              ...transformedOptions,
          ]
        : transformedOptions;

    return (
        <Box minWidth={230}>
            <Autocomplete
                id={id}
                multiple={isMultiValue}
                fullWidth={fullWidth}
                disabled={disabled || readOnly || field.disabled}
                label={intl.formatMessage({
                    defaultMessage: formatDefault,
                    id: formatId,
                })}
                helperText={readOnly && readOnlyMessage ? readOnlyMessage : undefined}
                value={autocompleteValue}
                onChange={(_, value) => {
                    if (isMultiValue && Array.isArray(value)) {
                        if (value.some((v: any) => v?.code === SELECT_ALL_CODE)) {
                            field.onChange(allSelected ? [] : transformedOptions.map(o => o.code));
                        } else {
                            field.onChange(value.map((v: any) => v.code));
                        }
                    } else if (value && 'code' in value) {
                        field.onChange(value.code);
                    } else {
                        field.onChange('');
                    }
                    onChange?.(_, value);
                }}
                options={autocompleteOptions}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                getOptionLabel={opt =>
                    opt.code === SELECT_ALL_CODE
                        ? opt.label
                        : getCGTCustomMMDispoOptionLabel(
                              opt.code,
                              opt.label,
                              name,
                              sessionData?.attributes?.company,
                          )
                }
                inputValue={filterValue}
                onOpen={() => {
                    setIsOpen(true);
                    setFilterValue('');
                }}
                onClose={() => {
                    setIsOpen(false);
                }}
                onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') {
                        setFilterValue(value);
                    } else if (!isOpen) {
                        setFilterValue(value);
                    }
                }}
                filterOptions={opts =>
                    opts.filter(o => {
                        // Keep the select-all pseudo-option visible only when not filtering
                        if (o.code === SELECT_ALL_CODE) {
                            return filterValue === '';
                        }
                        return getCGTCustomMMDispoOptionLabel(
                            o.code,
                            o.label,
                            name,
                            sessionData?.attributes?.company,
                        )
                            .toLowerCase()
                            .includes(filterValue.toLowerCase());
                    })
                }
                renderTags={(tagValue, getTagProps) => {
                    // Cap visible chips and collapse the rest into a "+n" chip to keep the input inline
                    const hiddenTags = tagValue.slice(MAX_VISIBLE_TAGS);
                    return (
                        <>
                            {tagValue.slice(0, MAX_VISIBLE_TAGS).map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return <Chip key={key} label={option.label} {...tagProps} />;
                            })}
                            {hiddenTags.length > 0 && (
                                <Tooltip title={hiddenTags.map(o => o.label).join(', ')}>
                                    <Chip label={`+${hiddenTags.length}`} />
                                </Tooltip>
                            )}
                        </>
                    );
                }}
                handleHomeEndKeys
                disablePortal={false}
                selectOnFocus
                blurOnSelect={!isMultiValue}
                disableCloseOnSelect={isMultiValue}
                clearOnBlur
                componentsProps={componentsProps}
                popupIcon={<KeyboardArrowDownIcon />}
            />
        </Box>
    );
}

export default SelectFieldElement;
